import { Link } from "react-router-dom";
import { Building2, Coins, FolderKanban, Mail, MapPin, Hash, User, Clock, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import { formatDate } from "@/lib/dateUtils";
import type { Organization } from "@/types/organization";
import { useOrganizationSeatSummaryQuery } from "@/hooks/queries/useSeatsQuery";

interface OverviewTabProps {
  organization: Organization;
}

const OverviewTab = ({ organization }: OverviewTabProps) => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const tz = useUserTimezone();
  const { data: seatSummary } = useOrganizationSeatSummaryQuery(organization.id);

  const fields = [
    { label: t("organizationOwner"), value: organization.owner || "-", icon: User },
    { label: t("organizationNumber"), value: organization.orgNumber || "-", icon: Hash },
    { label: t("contactEmail"), value: organization.contactEmail || "-", icon: Mail },
    { label: t("country"), value: organization.country || "-", icon: MapPin },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Coins size={14} />
              {t("organizationCreditBalance")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{organization.creditBalance.toFixed(2)}</p>
            {organization.lastCreditsUpdatedAt ? (
              <p className="mt-2 text-sm text-muted-foreground">
                {t("lastUpdated")}: {formatDate(organization.lastCreditsUpdatedAt, tz, language)}
              </p>
            ) : null}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users size={14} />
              {t("seats")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {seatSummary ? `${seatSummary.activeSeats} / ${seatSummary.availableSeats}` : "-"}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("seatsActive")}: {seatSummary?.activeSeats ?? 0} · {t("seatsAvailable")}: {seatSummary?.availableSeats ?? 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <FolderKanban size={14} />
              {t("organizationProjects")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{organization.projectCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock size={14} />
              {t("createdAt")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold">{formatDate(organization.createdAt, tz, language)}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 size={18} />
            {t("organizationDetails")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {organization.description ? <p className="text-sm text-muted-foreground">{organization.description}</p> : null}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((field) => (
              <div key={field.label} className="flex items-start gap-3 rounded-md border p-3">
                <field.icon className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">{field.label}</p>
                  <p className="font-medium">{field.value}</p>
                </div>
              </div>
            ))}
          </div>
          <Link to="/project" className="text-sm text-primary hover:underline">
            {t("viewProjects")}
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default OverviewTab;
